// WaroomPage.jsx - 移动端/桌面端分离版本
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Fade,
  Container,
  useMediaQuery,
  useTheme,
  Toolbar,
} from '@mui/material';
import AppAppBar from '../components/AppAppBar';
import { useState, useEffect, useRef } from 'react';
import AppTheme from '../AppTheme';
import CssBaseline from '@mui/material/CssBaseline';
// 导入拆分的组件
import DynamicBackground from '../components/DynamicBackground';
import MessageItem from '../components/MessageItem';
import PinnedMessage from '../components/PinnedMessage';
import ChatInput from '../components/ChatInput';
import StatusPanel from '../components/StatusPanel';
import NotificationsDialog from '../components/NotificationsDialog';
import ConnectionAlerts from '../components/ConnectionAlerts';
import { MobileChatToolbar } from '../components/MobileChatToolbar';

// 导入工具函数和Hook
import {
  API_BASE,
  checkAuthStatus,
  getCurrentUser,
  getAuthToken,
  normalizeImageUrl,
  compressImage,
  handleMentionClick,
  validateImageFile,
} from '../utils/constants';
import { useWebSocket } from '../hooks/useWebSocket';

export default function WaroomPage(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // 進入頁面 → 禁用滾動 (桌面端和移動端都禁用)
    document.body.style.overflow = "hidden";
    // 移動端額外禁用觸摸滾動
    if (isMobile) {
      document.body.style.touchAction = "none";
      document.documentElement.style.overflow = "hidden";
    }
    return () => {
      // 離開頁面 → 恢復滾動
      document.body.style.overflow = "auto";
      if (isMobile) {
        document.body.style.touchAction = "auto";
        document.documentElement.style.overflow = "auto";
      }
    };
  }, [isMobile]);

  // 基础状态
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // 聊天相关状态
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [images, setImages] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // 置顶消息和统计
  const [pinnedMessage, setPinnedMessage] = useState(null);
  const [replyStats, setReplyStats] = useState({});
  
  // 通知相关
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // 活跃度追踪
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  const chatBoxRef = useRef(null);

  // WebSocket消息处理
  const handleWebSocketMessage = (type, data) => {
    switch (type) {
      case 'new_message':
        setMessages(prev => {
          const newMessages = [...prev, data];
          updateReplyStats(newMessages);
          return newMessages;
        });
        scrollToBottom();
        break;
        
      case 'mention_notification':
        setNotifications(prev => [data, ...prev]);
        break;
    }
  };

  // 使用WebSocket Hook
  const {
    isConnected,
    isConnecting,
    disconnectReason,
    onlineCount,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    sendMessage: sendWebSocketMessage,
    cleanup: cleanupWebSocket
  } = useWebSocket(handleWebSocketMessage);

  // 统计回覆次数并找出最热门消息
  const updateReplyStats = (messagesList) => {
    const stats = {};
    let maxReplies = 0;
    let mostRepliedMessage = null;

    messagesList.forEach(message => {
      if (message.reply_to_id) {
        stats[message.reply_to_id] = (stats[message.reply_to_id] || 0) + 1;
        if (stats[message.reply_to_id] > maxReplies) {
          maxReplies = stats[message.reply_to_id];
          const repliedMessage = messagesList.find(msg => msg.id === message.reply_to_id);
          if (repliedMessage) {
            mostRepliedMessage = { ...repliedMessage, replyCount: maxReplies };
          }
        }
      }
    });

    setReplyStats(stats);
    
    if (maxReplies >= 10) {
      setPinnedMessage(mostRepliedMessage);
    } else {
      setPinnedMessage(null);
    }
  };
  
  // 载入聊天历史
  const loadChatHistory = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/chat/history?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const messagesList = data.messages || [];
        
        const normalizedMessages = messagesList.map(message => ({
          ...message,
          images: message.images ? message.images.map(normalizeImageUrl) : []
        }));
        
        setMessages(normalizedMessages);
        updateReplyStats(normalizedMessages);
      }
    } catch (error) {
      console.error('載入聊天歷史失敗:', error);
    }
  };
  
  // 滚动到底部
  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      setTimeout(() => {
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      }, 100);
    }
  };

  // 发送消息
  const sendMessage = async () => {
    if ((!input.trim() && images.length === 0) || !isConnected) return;
    
    const message = {
      type: 'chat_message',
      data: {
        message_text: input.trim(),
        images: images,
        reply_to_id: replyTo?.id || null,
      }
    };
    
    if (sendWebSocketMessage(message)) {
      setInput('');
      setImages([]);
      setReplyTo(null);
      setLastActivity(Date.now());
    }
  };
  
// 處理圖片上傳 - 增強版
const handleImageUpload = async (files) => {
  if (!files || files.length === 0) return;
  
  setUploading(true);
  const uploadedImages = [];
  const errors = [];
  
  // ✅ 限制一次最多上傳的數量
  const filesToProcess = Array.from(files).slice(0, 3 - images.length);
  
  for (let file of filesToProcess) {
    try {
      // ✅ 1. 驗證文件
      const validation = validateImageFile(file);
      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`);
        continue;
      }
      
      // ✅ 2. 壓縮圖片
      let compressedFile;
      try {
        compressedFile = await compressImage(file);
        console.log(`圖片壓縮成功: ${file.name}`, {
          原始大小: `${(file.size / 1024).toFixed(2)}KB`,
          壓縮後: `${(compressedFile.size / 1024).toFixed(2)}KB`
        });
      } catch (compressError) {
        console.error('圖片壓縮失敗:', compressError);
        errors.push(`${file.name}: 圖片處理失敗`);
        continue;
      }
      
      // ✅ 3. 上傳到服務器
      const formData = new FormData();
      formData.append('file', compressedFile);
      
      const token = getAuthToken();
      
      try {
        const response = await fetch(`${API_BASE}/upload-image`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          const fullImageUrl = normalizeImageUrl(data.image_url);
          uploadedImages.push(fullImageUrl);
          console.log('圖片上傳成功:', fullImageUrl);
        } else {
          const errorData = await response.json();
          errors.push(`${file.name}: ${errorData.detail || '上傳失敗'}`);
        }
      } catch (uploadError) {
        console.error('圖片上傳網絡錯誤:', uploadError);
        errors.push(`${file.name}: 網絡錯誤，請檢查連接`);
      }
      
    } catch (error) {
      console.error('處理圖片時發生未預期的錯誤:', error);
      errors.push(`${file.name}: 處理失敗`);
    }
  }
  
  // ✅ 4. 更新狀態
  if (uploadedImages.length > 0) {
    setImages(prev => [...prev, ...uploadedImages].slice(0, 3));
  }
  
  // ✅ 5. 顯示錯誤信息
  if (errors.length > 0) {
    alert('部分圖片上傳失敗:\n' + errors.join('\n'));
  }
  
  setUploading(false);
};
  
  // 回覆消息
  const handleReply = (message) => {
    setReplyTo(message);
  };
  
  // 清除通知
  const clearNotifications = () => {
    setNotifications([]);
    setShowNotifications(false);
  };

  // 初始化认证和数据
  useEffect(() => {
    const initializeAuth = async () => {
      if (checkAuthStatus()) {
        const user = await getCurrentUser();
        if (user) {
          setCurrentUser(user);
          setIsAuthenticated(true);
          await loadChatHistory();
        } else {
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };
    
    initializeAuth();
    
    return () => {
      cleanupWebSocket();
    };
  }, [cleanupWebSocket]);

  // 请求通知权限
  useEffect(() => {
    if (isAuthenticated && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <>
        <AppAppBar />
        <DynamicBackground isConnected={isConnected} />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 15
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <AppAppBar />
        <DynamicBackground isConnected={isConnected} />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            pt: 15
          }}
        >
          <Paper sx={{ p: 6, textAlign: 'center', maxWidth: 400, borderRadius: 3 }}>
            <Typography variant="h4" gutterBottom color="primary">
              登入後訪問
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
              本模組僅對註冊用戶開放，請先登入您的帳戶。
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.href = '/signin'}
              sx={{ borderRadius: 2 }}
            >
              前往登入
            </Button>
          </Paper>
        </Box>
      </>
    );
  }

  // 移动端布局
  if (isMobile) {
    return (
      <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <DynamicBackground isConnected={isConnected} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100dvh', // ✅ 使用動態視口高度
            pt: 0,
            overflow: 'hidden', // ✅ 防止整體溢出
          }}
        >
          <Container 
            maxWidth="lg"
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%', // ✅ 改為 100% 而不是 flex: 1
              overflow: 'hidden',
            }}            
          >
              {/* 移动端顶部工具栏 */}
              <MobileChatToolbar
                isConnected={isConnected}
                isConnecting={isConnecting}
                disconnectReason={disconnectReason}
                onConnect={connectWebSocket}
                onDisconnect={disconnectWebSocket}
                notifications={notifications}
                onShowNotifications={() => setShowNotifications(true)}
                onlineCount={onlineCount}
                onMenuClick={() => setDrawerOpen(true)}
                isAuthenticated={isAuthenticated}
              />
              
              {/* 聊天记录区 */}
              <Paper
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  background: isConnected 
                    ? 'rgba(0,0,0,0.7)' 
                    : 'rgba(255,255,255,0.9)',
                  border: `1px solid ${isConnected ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                  borderRadius: 2,
                  position: 'relative',
                  opacity: !isConnected && !isConnecting ? 0.7 : 1,
                  transition: 'all 0.3s ease-in-out',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  minHeight: 0, // ✅ 允許彈性收縮
                }}
                ref={chatBoxRef}
              >
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                  {/* 置顶消息 */}
                  {pinnedMessage && (
                    <PinnedMessage
                      message={pinnedMessage}
                      currentUser={currentUser}
                      onReply={handleReply}
                    />
                  )}
                  
                  {messages.length === 0 ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                        minHeight: 200,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color={isConnected ? 'rgba(255,255,255,0.6)' : 'text.secondary'}
                      >
                        {isConnected ? '歡迎來到戰情室！' : '點擊連接戰情室'}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={isConnected ? 'rgba(255,255,255,0.4)' : 'text.secondary'}
                      >
                        {isConnected ? '開始分享你的情報和見解吧' : '連線後即可開始即時聊天'}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0}>
                      {messages.map((message) => (
                        <MessageItem
                          key={message.id}
                          message={message}
                          currentUser={currentUser}
                          onReply={handleReply}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Paper>

              {/* ✅ 永遠固定在底部，添加安全區域處理 */}
              <Box 
                sx={{ 
                  flexShrink: 0, 
                  p: 1,
                  pb: 'max(8px, env(safe-area-inset-bottom))', // ✅ 處理底部安全區域
                  backgroundColor: 'background.default',
                }}
              >
              {/* 聊天输入区域 */}
                <ChatInput
                  isConnected={isConnected}
                  input={input}
                  setInput={setInput}
                  images={images}
                  setImages={setImages}
                  replyTo={replyTo}
                  setReplyTo={setReplyTo}
                  uploading={uploading}
                  onImageUpload={handleImageUpload}
                  onSendMessage={sendMessage}
                  isMobile={true}
                />
              </Box>
          </Container>

          {/* 通知彈窗 */}
          <NotificationsDialog
            open={showNotifications}
            onClose={() => setShowNotifications(false)}
            notifications={notifications}
            isConnected={isConnected}
            onClearNotifications={clearNotifications}
            onViewMessage={handleMentionClick}
          />
        </Box>
      </AppTheme>
    );
  }

  // 桌面端布局 (保持原样)
  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <DynamicBackground isConnected={isConnected} />
      <AppAppBar />
      <Fade in={true} timeout={800}>
        <Box
          sx={{
            minHeight: '100vh',
            pt: 12,
            pb: 3,
            color: isConnected ? '#fff' : 'inherit',
            transition: 'color 0.8s ease-in-out',
          }}
        >
          <Container maxWidth="lg" sx={{ mt:1 }}>
            <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', gap: 2 }}>
              
              {/* 左侧主聊天区域 */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* 连线状态提示 */}
                <ConnectionAlerts
                  isConnected={isConnected}
                  isConnecting={isConnecting}
                  disconnectReason={disconnectReason}
                  onConnect={connectWebSocket}
                />
                
                {/* 聊天记录区 */}
                <Paper
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    background: isConnected 
                      ? 'rgba(0,0,0,0.7)' 
                      : 'rgba(255,255,255,0.9)',
                    border: `1px solid ${isConnected ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                    borderRadius: 2,
                    position: 'relative',
                    opacity: !isConnected && !isConnecting ? 0.7 : 1,
                    transition: 'all 0.3s ease-in-out',
                  }}
                  ref={chatBoxRef}
                >
                  <Box sx={{ p: 2 }}>
                    {/* 置顶消息 */}
                    {pinnedMessage && (
                      <PinnedMessage
                        message={pinnedMessage}
                        currentUser={currentUser}
                        onReply={handleReply}
                      />
                    )}
                    
                    {messages.length === 0 ? (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          gap: 2,
                          minHeight: 300,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color={isConnected ? 'rgba(255,255,255,0.6)' : 'text.secondary'}
                        >
                          {isConnected ? '歡迎來到戰情室！' : '點擊連接戰情室'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={isConnected ? 'rgba(255,255,255,0.4)' : 'text.secondary'}
                        >
                          {isConnected ? '開始分享你的情報和見解吧' : '連線後即可開始即時聊天'}
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={0}>
                        {messages.map((message) => (
                          <MessageItem
                            key={message.id}
                            message={message}
                            currentUser={currentUser}
                            onReply={handleReply}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>

                {/* 聊天输入区域 */}
                <ChatInput
                  isConnected={isConnected}
                  input={input}
                  setInput={setInput}
                  images={images}
                  setImages={setImages}
                  replyTo={replyTo}
                  setReplyTo={setReplyTo}
                  uploading={uploading}
                  onImageUpload={handleImageUpload}
                  onSendMessage={sendMessage}
                  isMobile={false}
                />
              </Box>

              {/* 右側狀態面板 */}
              <StatusPanel
                isConnected={isConnected}
                isConnecting={isConnecting}
                disconnectReason={disconnectReason}
                onlineCount={onlineCount}
                notifications={notifications}
                lastActivity={lastActivity}
                onConnect={connectWebSocket}
                onDisconnect={disconnectWebSocket}
                onShowNotifications={() => setShowNotifications(true)}
                username = {currentUser.username}
              />
            </Box>
          </Container>

          {/* 通知彈窗 */}
          <NotificationsDialog
            open={showNotifications}
            onClose={() => setShowNotifications(false)}
            notifications={notifications}
            isConnected={isConnected}
            onClearNotifications={clearNotifications}
            onViewMessage={handleMentionClick}
          />
        </Box>
      </Fade>
    </AppTheme>
  );
};